import { createClient } from '@supabase/supabase-js';
import { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import type { Database } from '../types/supabase';
import type { User } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Environment check:', {
    hasUrl: !!supabaseUrl,
    hasKey: !!supabaseAnonKey,
    mode: import.meta.env.MODE
  });
  throw new Error('Missing Supabase environment variables');
}

// Simple, clean Supabase client
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
  }
});


// Ultra-simple auth hook that just works
export const useSimpleAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    let isMounted = true;
    
    // Set a timeout to prevent infinite loading
    const loadingTimeout = setTimeout(() => {
      if (isMounted) {
        setLoading(false);
      }
    }, 3000); // 3 second timeout
    
    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!isMounted) return;
      
      setUser(session?.user ?? null);
      setIsAdmin(!!session?.user);
      setLoading(false);
      clearTimeout(loadingTimeout);
      
      // Trigger profile update event when user signs in
      if (session?.user) {
        window.dispatchEvent(new CustomEvent('profile-updated'));
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!isMounted) return;
      
      setUser(session?.user ?? null);
      setIsAdmin(!!session?.user);
      setLoading(false);
      clearTimeout(loadingTimeout);
      
      // Trigger profile update event when auth state changes
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        window.dispatchEvent(new CustomEvent('profile-updated'));
      }
    });

    return () => {
      isMounted = false;
      clearTimeout(loadingTimeout);
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  return {
    user,
    loading,
    isAdmin,
    signIn,
    signOut
  };
};

// Enhanced input sanitization functions with DOMPurify
export const sanitizeInput = (input: string): string => {
  if (!input) return '';
  
  // First trim whitespace
  const trimmed = input.trim();
  
  // Use DOMPurify for comprehensive sanitization
  return DOMPurify.sanitize(trimmed, {
    ALLOWED_TAGS: [], // No HTML tags allowed in plain text input
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true
  });
};

export const sanitizeHtml = (html: string): string => {
  if (!html) return '';
  
  // Configure DOMPurify for rich text content
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'a', 'img'
    ],
    ALLOWED_ATTR: [
      'href', 'src', 'alt', 'title', 'class', 'id'
    ],
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
    ADD_ATTR: ['target'],
    ADD_TAGS: [],
    KEEP_CONTENT: true
  });
};

// Additional sanitization for specific use cases
export const sanitizeUrl = (url: string): string => {
  if (!url) return '';
  
  // Only allow http, https, and mailto URLs
  const sanitized = DOMPurify.sanitize(url, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    ALLOWED_URI_REGEXP: /^(https?|mailto):/i,
    KEEP_CONTENT: true
  });
  
  return sanitized;
};

export const sanitizeFilename = (filename: string): string => {
  if (!filename) return '';
  
  // Remove dangerous characters and limit length
  return filename
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .substring(0, 255)
    .trim();
};